import { TranslationKey } from '@Localization';
import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import { ACDLError, ForbiddenError } from '../../__legacy__/models/error.model';
import { NotificationLevel, ToastNotification } from '../../__legacy__/modules/notifications/models/notification.model';
import { INotificationsService } from '../../__legacy__/modules/notifications/services/notifications.service';
import { IOverlayLoaderStore } from '../common/OverlayLoader.store';
import { AdminStore, IAdminStore, IWorkspace } from './admin.store';
import { IAdminApiService } from './services/adminApi.service';

export interface IAdminService {
  store: AdminStore;

  // fetching
  refreshAsync(search: string): Promise<void | ACDLError>;
  changeWorkspaceLimitAsync(workspace: IWorkspace): Promise<void | ACDLError>;

  // actions
  changeOrder(orderBy: string | undefined, orderType: string | undefined, search: string): Promise<void>;
  changePagination(pageNumber: number, pageSize: number, search: string): Promise<void>;
  initPageSize(initialPageSize: number): void;
  workspacesListSearchChange(search: string): Promise<void>;
}

export namespace IAdminService {
  export const $: interfaces.ServiceIdentifier<IAdminService> = Symbol('ADMIN_SERVICE');
}

@injectable()
export class AdminService implements IAdminService {
  constructor(
    @inject(IAdminStore.$) public readonly store: AdminStore,
    @inject(IAdminApiService.$) public readonly apiService: IAdminApiService,
    @inject(INotificationsService.$) private readonly notificationsService: INotificationsService,
    @inject(IOverlayLoaderStore.$) private readonly overlayLoaderStore: IOverlayLoaderStore
  ) {
    makeObservable(this);
  }

  initPageSize(initialPageSize: number): void {
    this.store.workspacesPaging.pageSize = initialPageSize;
    this.store.workspacesPaging.pageNumber = 1;
  }

  @action
  async refreshAsync(search: string): Promise<void> {
    this.overlayLoaderStore.enableLoader('workspaces-list-table');
    await this.getWorkspacesAsync(search);
    this.overlayLoaderStore.disableLoader('workspaces-list-table');
  }

  @action
  async getWorkspacesAsync(search: string): Promise<void> {
    const { pageNumber, pageSize, orderBy, orderType } = this.store.workspacesPaging;

    const request = {
      pageNumber,
      pageSize,
      orderBy,
      orderType,
      search: search || '',
    };

    this.store.workspaces.replace([]);

    const result = await this.apiService.getWorkspacesAsync(request);

    if (result instanceof ACDLError) {
      await this.notifyAboutApiError(result, 'get_workspaces_failed');
      return;
    }

    if (result.pagesCount < result.pageNumber) {
      if (result.pagesCount === 0) {
        this.store.workspacesPaging.totalCount = 0;
        return;
      }
      this.store.workspacesPaging.pageNumber = result.pagesCount;
      await this.getWorkspacesAsync(search);
    } else {
      let costsResult = await this.apiService.getWorkspacesCostsAsync(result.data.map((workspace) => workspace.id));

      if (costsResult instanceof ACDLError) {
        costsResult = {};
      }

      this.store.workspaces.replace(
        result.data.map((workspace) => ({
          ...workspace,
          totalCostActual: costsResult[workspace.id]?.total_cost_actual ?? 0,
          totalCostCalculated: costsResult[workspace.id]?.total_cost_calculated ?? 0,
        }))
      );

      this.store.workspacesPaging = {
        ...this.store.workspacesPaging,
        orderBy,
        orderType,
        pageNumber: result.pageNumber,
        pagesCount: result.pagesCount,
        totalCount: result.totalCount,
      };
    }
  }

  @action
  async changeWorkspaceLimitAsync(workspace: IWorkspace): Promise<void | ACDLError> {
    const request = {
      workspaceId: workspace.id,
      cloudCostLimit: workspace.cloudCostsLimit,
    };

    await this.overlayLoaderStore.withLoaderAsync('change-workspace-limit', async (): Promise<void> => {
      const result = await this.apiService.updateWorkspaceLimitAsync(request);

      if (result instanceof ACDLError) {
        await this.notifyAboutApiError(result, 'update_workspace_limit_failed');
      }

      this.store.workspaces.replace(this.store.workspaces.map((item) => (item.id === workspace.id ? workspace : item)));
    });
  }

  @action
  async changePagination(pageNumber: number, pageSize: number, search: string): Promise<void> {
    this.store.workspacesPaging.pageNumber = pageNumber;
    this.store.workspacesPaging.pageSize = pageSize;
    await this.refreshAsync(search);
  }

  @action
  async changeOrder(orderBy: string | undefined, orderType: string | undefined, search: string): Promise<void> {
    this.store.workspacesPaging.orderBy = orderBy || '';
    this.store.workspacesPaging.orderType = orderType || '';
    await this.refreshAsync(search);
  }

  async workspacesListSearchChange(search: string): Promise<void> {
    this.store.workspacesPaging = {
      ...this.store.workspacesPaging,
      search,
      pageNumber: 1,
    };

    await this.overlayLoaderStore.withLoaderAsync('workspaces-list-table', async (): Promise<void> => {
      await this.getWorkspacesAsync(search);
    });
  }

  async notifyAboutApiError(result: unknown, errorCode: TranslationKey, dontShowBadRequest: boolean = false): Promise<void> {
    if (result instanceof ACDLError && !(result instanceof ForbiddenError) && (!dontShowBadRequest || !result.isBadRequest())) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, errorCode));
    }
  }
}
